import { useState } from 'react';
import { ExternalLink, Sun, Moon, Menu, ChevronRight } from 'lucide-react';
import { useRouterState } from '@tanstack/react-router';
import { getBreadcrumbTrail } from '@/lib/admin-navigation';
import { useSidebar } from './sidebar-context';

function useTheme() {
  const [dark, setDark] = useState(() =>
    typeof document !== 'undefined' && document.documentElement.classList.contains('dark'),
  );
  const toggle = () => {
    const next = !dark;
    setDark(next);
    document.documentElement.classList.toggle('dark', next);
    try { localStorage.setItem('logink-theme', next ? 'dark' : 'light'); } catch { /* ignore */ }
  };
  return { dark, toggle };
}

export function TopBar() {
  const { toggle: toggleSidebar } = useSidebar();
  const { dark, toggle: toggleTheme } = useTheme();
  const pathname = useRouterState({ select: s => s.location.pathname });
  const tab = useRouterState({
    select: s => {
      const raw = (s.location.search as { tab?: unknown }).tab;
      return typeof raw === 'string' ? raw : undefined;
    },
  });
  const { labels, tail } = getBreadcrumbTrail(pathname, tab);

  return (
    <header className="sticky top-0 z-30 h-13 border-b border-border/80 bg-background/88 backdrop-blur-md">
      <div className="h-full px-3.5 sm:px-5 lg:px-8 flex items-center gap-2.5">
        <button
          type="button"
          onClick={toggleSidebar}
          aria-label="메뉴 열기"
          className="md:hidden -ml-1 h-8 w-8 inline-flex items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
        >
          <Menu className="h-4.5 w-4.5" strokeWidth={1.75} />
        </button>

        <nav aria-label="경로" className="flex items-center gap-1.5 min-w-0 text-[13.5px]">
          {labels.map((label, i) => (
            <span key={`l-${i}`} className="flex items-center gap-1.5 min-w-0">
              {i > 0 && <ChevronRight className="h-3.5 w-3.5 text-muted-foreground/50 shrink-0" strokeWidth={1.75} />}
              <span
                className={
                  i === labels.length - 1 && tail.length === 0
                    ? 'font-medium text-foreground truncate tracking-tight'
                    : 'text-muted-foreground truncate tracking-tight'
                }
              >
                {label}
              </span>
            </span>
          ))}
          {tail.map((seg, i) => (
            <span key={`t-${i}`} className="flex items-center gap-1.5 min-w-0">
              <ChevronRight className="h-3.5 w-3.5 text-muted-foreground/50 shrink-0" strokeWidth={1.75} />
              <span className="text-muted-foreground truncate max-w-[24ch] sm:max-w-[36ch] font-mono text-[12.5px]">
                {seg}
              </span>
            </span>
          ))}
        </nav>

        <div className="ml-auto flex items-center gap-1 sm:gap-1.5">
          <button
            type="button"
            onClick={toggleTheme}
            aria-label="테마 전환"
            className="h-8 w-8 inline-flex items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
          >
            {dark ? <Sun className="h-4 w-4" strokeWidth={1.75} /> : <Moon className="h-4 w-4" strokeWidth={1.75} />}
          </button>

          <a
            href="https://zerry.co.kr"
            target="_blank"
            rel="noopener"
            className="hidden sm:inline-flex items-center gap-1.5 h-8 px-2.5 rounded-md border border-border bg-card text-[12.5px] text-muted-foreground hover:text-foreground hover:border-foreground/20 shadow-xs transition-colors"
          >
            블로그 열기
            <ExternalLink className="h-3.5 w-3.5" strokeWidth={1.75} />
          </a>
          <a
            href="https://zerry.co.kr"
            target="_blank"
            rel="noopener"
            aria-label="블로그 열기"
            className="sm:hidden h-8 w-8 inline-flex items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground"
          >
            <ExternalLink className="h-4 w-4" strokeWidth={1.75} />
          </a>
        </div>
      </div>
    </header>
  );
}
